import { Button } from "~/components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { ExportDropdown } from "./ExportDropdown";
import { ExportType } from "~/modules/downloads/types";
import { ResultCountDisplay } from "./ResultCountDisplay";
import { ApiQuery, ApiQueryExample } from "../types";
import { ExamplesDropdown } from "./ExamplesDropdown";
import {
    faMagnifyingGlass,
    faMagnifyingGlassPlus,
    faSquareTerminal,
} from "@fortawesome/pro-solid-svg-icons";
import { trackApplySearch, trackDiscardSearch } from "../analytics";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { CopyToClipboard } from "~/components/CopyToClipboard";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { Separator } from "~/components/ui/separator";
import { ShareIcon } from "lucide-react";

// // // //

function ProSearchControls(props: {
    isProSearch: boolean;
    enableProSearch: boolean;
    applyQuery: () => void;
    hasChanges: boolean;
    clearChanges: () => void;
}) {
    const {
        enableProSearch,
        isProSearch,
        hasChanges,
        clearChanges,
        applyQuery,
    } = props;
    return (
        <>
            {enableProSearch && isProSearch && hasChanges && (
                <div className="flex items-center gap-2 @md:gap-3">
                    <Button
                        size="xs"
                        disabled={!hasChanges}
                        variant="warningLight"
                        onClick={() => {
                            // toggleEditing();
                            trackDiscardSearch();
                            clearChanges();
                        }}
                        className="py-2 px-3 @md:px-4"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        Discard
                    </Button>
                    <Button
                        size="xs"
                        variant="successLight"
                        disabled={!hasChanges}
                        onClick={() => {
                            trackApplySearch();
                            applyQuery();
                        }}
                        className="py-2 px-3 @md:px-4"
                    >
                        <FontAwesomeIcon icon={faCheck} />
                        Apply Search
                    </Button>
                </div>
            )}
        </>
    );
}

function ProSearchResultCount(props: {
    isProSearch: boolean;
    enableProSearch: boolean;
    hasChanges: boolean;
    internalQuery: null | {
        loading: boolean;
        resultCount: number;
    };
}) {
    const { enableProSearch, isProSearch, hasChanges, internalQuery } = props;

    if (enableProSearch && isProSearch && internalQuery && hasChanges) {
        return (
            <ResultCountDisplay
                defaultVariant="green"
                loading={internalQuery.loading}
                resultCount={internalQuery.resultCount}
            />
        );
    }

    return null;
}

// // // //

function ShareButton(props: { url: string; disabled: boolean }) {
    const { url, disabled } = props;
    const { addToaster } = useToaster();

    return (
        <FeatureFlag feature={FeatureNames.enableExperimentalFeatures}>
            <CopyToClipboard
                onCopy={() => {
                    // ENHANCEMENT - add analytics here
                    addToaster({
                        variant: "success",
                        message: "Copied sharable URL to clipboard",
                    });
                }}
            >
                {({ copyToClipboard }) => (
                    <Button
                        size="xs"
                        disabled={disabled}
                        variant="outline"
                        className="shadow-sm border-muted-foreground"
                        onClick={() => {
                            copyToClipboard(url);
                        }}
                    >
                        <ShareIcon />
                        Share
                    </Button>
                )}
            </CopyToClipboard>
        </FeatureFlag>
    );
}

// // // //

// TODO - clean up these props + logic, split into a series of smaller components
// TODO - the export + API + changeMode features should be disabled when "editing" is enabled -> add "tooltips" <---- all the more reason to split things up
// TODO - add "Reset Filters" button + callback prop
// TODO - add props.children to this to decouple everything a bit...?
// TODO - add Total Results + Loading State for current query?\
export interface Props {
    appliedQuery: ApiQuery;
    isProSearch: boolean;
    enableProSearch: boolean;
    enableBasicFilters: boolean;
    onChangeMode: () => void;
    status?: {
        loading: boolean;
        resultCount: number;
    };
    onExport?: (exportType: ExportType) => void;
    onClickApi?: () => void;
    applyQuery: () => void;
    hasChanges: boolean;
    clearChanges: () => void;
    // // // //
    // ApiQueryExamples
    queryExamples: ApiQueryExample[];
    onSelectQueryExample: (selectedExample: ApiQueryExample) => void;
    // // // //
    // TODO - must be renamed
    internalQuery?: {
        loading: boolean;
        resultCount: number;
    };
    sharing?: {
        enabled: boolean;
        url: string;
    };
    title?: string;
}

// TODO - decouple this from "Pro Search / Filter" logic
// This should *only* be used for "Pro Search"
export function FilterControls(props: Props) {
    const {
        isProSearch,
        onChangeMode,
        enableProSearch,
        enableBasicFilters,
        status,
        onClickApi,
        onExport,
        applyQuery,
        hasChanges,
        clearChanges,
        queryExamples,
        onSelectQueryExample,
        internalQuery = null,
        title = null,
        sharing = {
            enabled: false,
            url: "",
        },
    } = props;

    const hasSecondGroup =
        onExport !== undefined ||
        onClickApi !== undefined ||
        sharing.enabled ||
        queryExamples.length > 0;

    let headerText = isProSearch ? "Search" : "Filter";
    if (title !== null) {
        headerText = title;
    }

    // const resultNoun = title !== null ? title.toLowerCase().replace(/s$/, "") : "result";

    return (
        // `@container` scopes the `@*:` variants below to the width of the controls
        // themselves rather than the viewport. These controls render inside narrow
        // content columns (e.g. the API Tools endpoint pages, which sit beside a
        // 16rem sidebar), where viewport breakpoints report "desktop" for a
        // container that is nowhere near wide enough to lay the controls out in a row.
        <div className="@container w-full select-none">
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                {/* Primary controls group - Header, Status, and ProSearch controls */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 min-w-0">
                    {/* Header and status */}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <p className="text-2xl text-foreground">{headerText}</p>

                        {/* Current results */}
                        {status && (
                            <ResultCountDisplay
                                loading={status.loading}
                                resultCount={status.resultCount}
                                defaultVariant="blue"
                            />
                        )}
                    </div>

                    {/* ProSearch controls */}
                    {enableProSearch && isProSearch && hasChanges && (
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Purely a divider between the header and these
                                controls - drop it below the width where the two
                                still share a line, so it doesn't leave a phantom
                                indent at the start of a wrapped row */}
                            <Separator
                                orientation="vertical"
                                className="hidden @3xl:block min-h-6 bg-muted-foreground"
                            />

                            <ProSearchControls
                                isProSearch={isProSearch}
                                enableProSearch={enableProSearch}
                                applyQuery={applyQuery}
                                hasChanges={hasChanges}
                                clearChanges={clearChanges}
                            />

                            <ProSearchResultCount
                                isProSearch={isProSearch}
                                enableProSearch={enableProSearch}
                                hasChanges={hasChanges}
                                internalQuery={internalQuery}
                            />
                        </div>
                    )}
                </div>

                {/* Secondary controls group - Action buttons.
                    Wraps below the header once the two groups no longer fit on
                    one line, then wraps internally as the container keeps
                    narrowing. Every control here is sized to its own content so
                    the wrapping stays even. */}
                {hasSecondGroup && (
                    <div className="flex flex-wrap items-center gap-2 @md:gap-3">
                        {/* Mode switcher */}
                        {enableProSearch && enableBasicFilters && (
                            <Button
                                size="xs"
                                disabled={status?.loading || false}
                                variant="outline"
                                className="shadow-sm border-muted-foreground"
                                onClick={() => {
                                    onChangeMode();
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={
                                        isProSearch
                                            ? faMagnifyingGlass
                                            : faMagnifyingGlassPlus
                                    }
                                    className="h-3 mr-2"
                                />
                                {isProSearch
                                    ? "Switch to Basic Filters"
                                    : "Switch to Pro Search"}
                            </Button>
                        )}

                        {queryExamples.length > 0 && (
                            <ExamplesDropdown
                                disabled={status?.loading || false}
                                examples={queryExamples}
                                onSelectExample={onSelectQueryExample}
                            />
                        )}

                        {sharing.enabled && (
                            <ShareButton
                                url={sharing.url}
                                disabled={status?.loading || false}
                            />
                        )}

                        {props.onClickApi && (
                            <Button
                                size="xs"
                                variant="outline"
                                disabled={status?.loading || false}
                                className="shadow-sm border-muted-foreground"
                                data-testid="visualization-controls--api-tools-button"
                                onClick={() => {
                                    if (props.onClickApi) {
                                        props.onClickApi();
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faSquareTerminal} />
                                Code Samples
                            </Button>
                        )}

                        {props.onExport && (
                            <ExportDropdown
                                disabled={status?.loading || false}
                                onExport={(exportType) => {
                                    if (props.onExport) {
                                        props.onExport(exportType);
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
